import { useState, useCallback } from 'react'

const STORAGE_KEY = 'chckup-dismissed-alerts'

function readDismissedKeys(): string[] {
    try {
        if (typeof window !== 'undefined') {
            const raw = window.localStorage.getItem(STORAGE_KEY)
            return raw ? (JSON.parse(raw) as string[]) : []
        }
    } catch (error) {
        console.error(
            'Failed to read dismissed alerts from localStorage:',
            error,
        )
    }
    return []
}

function writeDismissedKeys(keys: string[]) {
    try {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys))
        }
    } catch (error) {
        console.error(
            'Failed to write dismissed alerts to localStorage:',
            error,
        )
    }
}

/**
 * Manages all dismissed temp-alert keys in a single localStorage entry.
 * Returns the set of dismissed keys and a function to dismiss an alert by key.
 */
export function useDismissedAlerts(): {
    dismissedKeys: Set<string>
    dismiss: (key: string) => void
} {
    const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(
        () => new Set(readDismissedKeys()),
    )

    const dismiss = useCallback((key: string) => {
        setDismissedKeys(prev => {
            const next = new Set(prev)
            next.add(key)
            writeDismissedKeys([...next])
            return next
        })
    }, [])

    return { dismissedKeys, dismiss }
}
